import React, { useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { FormikProps } from 'formik'
import {
  admin_sauce_dosing_mode_comments_path,
  admin_sauce_dosing_mode_path,
  admin_sauce_dosing_modes_path,
  copy_sources_admin_sauce_dosing_modes_path,
  create_draft_from_version_admin_sauce_dosing_mode_path,
  create_new_version_draft_admin_sauce_dosing_mode_path,
  edit_admin_sauce_dosing_mode_path,
  publish_admin_sauce_dosing_mode_path,
  tag_options_admin_sauce_dosing_mode_comments_path
} from '../../../routes'
import { t } from '../../i18n'
import CommentsPanel from '../shared/comments/CommentsPanel'
import { renderRatingMeta } from '../shared/comments/RatingSummary'
import { CommentsSection } from '../shared/comments/types'
import SectionTitle from '../shared/sectionTitle'
import { DosingLimits } from '../shared/dosingLimits'
import DosingModeUsagePanel, {
  DosingModeUsage
} from '../shared/dosing_modes/DosingModeUsagePanel'
import extractServerErrors from '../shared/extractServerErrors'
import { railsFieldErrors } from '../shared/forms/railsFieldErrors'
import ConfirmModal from '../shared/versioning/ConfirmModal'
import CopyFromModal from '../shared/versioning/CopyFromModal'
import PublishConfirmModal from '../shared/versioning/PublishConfirmModal'
import PublishImpactBody from '../shared/versioning/PublishImpactBody'
import ReadOnlyNotice from '../shared/versioning/ReadOnlyNotice'
import VersionActionsBar from '../shared/versioning/VersionActionsBar'
import VersionsPanel from '../shared/versioning/VersionsPanel'
import { VersionStatusBadge } from '../shared/versioning/badges'
import useCopyFromSource from '../shared/versioning/useCopyFromSource'
import useSaveAndPublish from '../shared/versioning/useSaveAndPublish'
import SauceDosingModeForm, { FORM_ID } from './SauceDosingModeForm'
import {
  CopySourceSauceDosingMode,
  SauceDosingModeResponse,
  SauceDosingModeValues,
  VersioningProps,
  copyParameters,
  emptyMode,
  toFormValues,
  toPayload
} from './types'

interface Props {
  sauceDosingMode?: SauceDosingModeResponse
  limits: DosingLimits
  readOnly?: boolean
  commentsSection?: CommentsSection
  versioning?: VersioningProps
  usage?: DosingModeUsage
}

interface SaveErrors {
  errors?: string[]
  field_errors?: Record<string, string[]>
}

interface Banner {
  kind: 'success' | 'danger'
  messages: string[]
}

const SauceDosingModePage = ({
  sauceDosingMode,
  limits,
  readOnly = false,
  commentsSection,
  versioning,
  usage
}: Props) => {
  const [mode, setMode] = useState<SauceDosingModeResponse | null>(
    sauceDosingMode ?? null
  )
  const [banner, setBanner] = useState<Banner | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false)
  const formikRef = useRef<FormikProps<SauceDosingModeValues>>(null)
  const initialValues = useMemo(
    () => (mode ? toFormValues(mode) : emptyMode()),
    [mode]
  )
  const { confirmPublish, afterSaved, cancelPending } =
    useSaveAndPublish<SauceDosingModeValues>({
      formikRef,
      publishUrl: publish_admin_sauce_dosing_mode_path,
      publishFailedText: t('versioning.publish_failed'),
      onPublishError: (messages) => {
        setSubmitting(false)
        setBanner({ kind: 'danger', messages })
      }
    })
  const copyFrom = useCopyFromSource(
    formikRef,
    (source: CopySourceSauceDosingMode) => {
      const formik = formikRef.current
      if (formik) formik.setValues(copyParameters(formik.values, source))
    }
  )

  const handleSubmit = async (values: SauceDosingModeValues) => {
    const body = toPayload(values)
    setSubmitting(true)

    try {
      if (!mode) {
        const created = await axios.post<SauceDosingModeResponse>(
          admin_sauce_dosing_modes_path(),
          body
        )
        window.location.href = edit_admin_sauce_dosing_mode_path(
          created.data.id
        )
        return
      }
      const response = await axios.put<SauceDosingModeResponse>(
        admin_sauce_dosing_mode_path(mode.id),
        body
      )
      setMode(response.data)
      if (afterSaved(response.data.id)) return
      setSubmitting(false)
      setBanner({
        kind: 'success',
        messages: [t('sauce_dosing_modes.save_successful')]
      })
    } catch (error) {
      cancelPending()
      setSubmitting(false)
      if (
        axios.isAxiosError<SaveErrors>(error) &&
        error.response?.data?.field_errors
      ) {
        formikRef.current?.setStatus({
          serverErrors: railsFieldErrors(error.response.data.field_errors)
        })
      }
      const headline = t('sauce_dosing_modes.save_failed')
      const details = extractServerErrors(error, headline)
      setBanner({
        kind: 'danger',
        messages: details[0] === headline ? details : [headline, ...details]
      })
    }
  }

  const confirmAndPublish = () => {
    setShowPublishConfirm(false)
    confirmPublish('publish')
  }

  return (
    <>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <SectionTitle
            rows={[
              mode ? 'Edit' : 'New',
              t('sauce_dosing_modes.sauce_dosing_mode')
            ]}
          />
          {mode && (
            <span className="ms-3">
              <VersionStatusBadge
                status={mode.status}
                versionNumber={mode.version_number}
              />
            </span>
          )}
        </div>
        <VersionActionsBar
          actions={versioning?.actions}
          readOnly={readOnly}
          submitting={submitting}
          formId={FORM_ID}
          indexHref={admin_sauce_dosing_modes_path()}
          deleteDraftUrl={
            mode ? admin_sauce_dosing_mode_path(mode.id) : undefined
          }
          createNewVersionUrl={
            mode
              ? create_new_version_draft_admin_sauce_dosing_mode_path(mode.id)
              : undefined
          }
          deleteConfirmBody={t('sauce_dosing_modes.delete_draft_confirm_body')}
          onPublish={() => setShowPublishConfirm(true)}
        >
          <button
            type="button"
            className="btn btn-secondary inverted me-2"
            onClick={copyFrom.openPicker}
          >
            {t('sauce_dosing_modes.copy_from_mode.button')}
          </button>
        </VersionActionsBar>
      </div>
      {readOnly && mode && <ReadOnlyNotice />}
      <div className="card card-robis">
        <div className="dosing-mode-layout">
          <div className="dosing-mode-layout__form">
            {banner && (
              <div className={`alert alert-${banner.kind}`} role="alert">
                {banner.messages.map((message, index) => (
                  <div key={index}>{message}</div>
                ))}
              </div>
            )}
            <SauceDosingModeForm
              initialValues={initialValues}
              readOnly={readOnly}
              formikRef={formikRef}
              limits={limits}
              onSubmit={handleSubmit}
            />
          </div>
          {mode && (versioning || commentsSection) && (
            <div className="dosing-mode-layout__side">
              {versioning && (
                <VersionsPanel
                  versions={versioning.versions}
                  canRestore={versioning.actions.canRestore}
                  versionHref={(version) =>
                    edit_admin_sauce_dosing_mode_path(version.id)
                  }
                  restoreUrl={(version) =>
                    create_draft_from_version_admin_sauce_dosing_mode_path(
                      version.id
                    )
                  }
                  renderMeta={renderRatingMeta}
                />
              )}
              {commentsSection && (
                <CommentsPanel
                  commentsUrl={admin_sauce_dosing_mode_comments_path(mode.id)}
                  tagOptionsUrl={tag_options_admin_sauce_dosing_mode_comments_path(
                    mode.id
                  )}
                  initialComments={commentsSection.comments}
                  abilities={commentsSection.abilities}
                  tagging={commentsSection.tagging}
                  placeholder={t('sauce_dosing_modes.comment_placeholder')}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {mode && usage && <DosingModeUsagePanel usage={usage} />}
      {copyFrom.pickerOpen && (
        <CopyFromModal<CopySourceSauceDosingMode>
          url={copy_sources_admin_sauce_dosing_modes_path()}
          params={{ exclude_sauce_dosing_mode_id: mode?.id ?? null }}
          scope="sauce_dosing_modes.copy_from_mode"
          onSelect={copyFrom.handlePicked}
          onClose={copyFrom.closePicker}
        />
      )}
      {copyFrom.overwriteSource && (
        <ConfirmModal
          title={t('sauce_dosing_modes.copy_from_mode.overwrite_title')}
          confirmActions={[
            {
              label: t('sauce_dosing_modes.copy_from_mode.overwrite_confirm'),
              className: 'btn btn-primary',
              onConfirm: copyFrom.confirmOverwrite
            }
          ]}
          onClose={copyFrom.cancelOverwrite}
        >
          <p className="mb-0">
            {t('sauce_dosing_modes.copy_from_mode.overwrite_text')}
          </p>
        </ConfirmModal>
      )}
      {showPublishConfirm && versioning && (
        <PublishConfirmModal
          onPublish={confirmAndPublish}
          onClose={() => setShowPublishConfirm(false)}
        >
          <PublishImpactBody
            usedIntro={t('sauce_dosing_modes.publish_confirm_products')}
            usedItems={versioning.usedByProducts}
            noUsageText={t('sauce_dosing_modes.publish_confirm_no_products')}
            locationsIntro={t('sauce_dosing_modes.publish_confirm_locations')}
            locations={versioning.affectedLocations}
            noLocationsText={t(
              'sauce_dosing_modes.publish_confirm_no_locations'
            )}
          />
        </PublishConfirmModal>
      )}
    </>
  )
}

export default SauceDosingModePage
